/**
 * @fileoverview CRM and third-party integration management actions
 * @module IntegrationsActions
 */

import apiClient from "../utils/apiClient";
import useSWR from "swr";

export const IntegrationsAPI = {
  useGetCRM,
  updateCRM,
  deleteCRM,
  removeIntegration,
  useFeeds,
  updateEB,
  updateWiggot,
  useGetPropertyErrors,
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - Return if the agency id is valid
 */

/** @function
 * @name updateEB
 * @description - fetching to the EB API
 * @returns {SWRObject} - swr Object
 */

/**
 * Updates EB (Easy Broker) integration settings
 * @function
 * @name updateEB
 * @memberof module:IntegrationsActions
 * @param {Object} data - EB integration configuration data
 * @param {string} data.agency_id - The Easy Broker agency ID
 * @returns {Promise<Object>} The API response with updated integration settings
 */
async function updateEB(data) {
  return await apiClient.post("clients/crm/agency/", data);
}

/**
 * Updates Wiggot integration settings
 * @function
 * @name updateWiggot
 * @memberof module:IntegrationsActions
 * @param {Object} data - Wiggot integration configuration data
 * @returns {Promise<Object>} The API response with updated integration settings
 */
async function updateWiggot(data) {
  return await apiClient.post("clients/crm/wiggot/", data);
}
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 */

/**
 * Hook to fetch CRM configuration from the client
 * @function
 * @name useGetCRM
 * @memberof module:IntegrationsActions
 * @returns {SWRObject} SWR Object containing CRM data
 */

function useGetCRM() {
  const fetcher = (url) => apiClient.get(url).then((res) => res?.data);
  const { data, error, mutate } = useSWR("clients/pro/link/crm/", fetcher);
  return { data, error, mutate };
}
/**
 * Updates the CRM configuration for the client
 * @function
 * @name updateCRM
 * @memberof module:IntegrationsActions
 * @param {Object} data - CRM configuration values
 * @returns {Promise<Object>} API response
 */
async function updateCRM(data) {
  return apiClient.patch("clients/pro/link/crm/", data);
}

/**
 * Deletes the CRM configuration for the client
 * @function
 * @name deleteCRM
 * @memberof module:IntegrationsActions
 * @returns {Promise<void>}
 */
async function deleteCRM() {
  try {
    await apiClient.delete("clients/pro/link/crm/");
  } catch (err) {
    $log.error(err);
  }
}
/**
 * Removes the integration from the client with confirmation dialog
 * @function
 * @name removeIntegration
 * @memberof module:IntegrationsActions
 * @returns {Promise<void>}
 * @throws {Error} When user cancels the action
 */

async function removeIntegration() {
  if (window.confirm("¿Estás seguro que quieres eliminar esta integración?")) {
    await apiClient.delete("/clients/pro/link/crm/");
  } else {
    throw new Error("Cancelled action");
  }
}
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 */
/**
 * Hook to obtain active property feeds
 * @function
 * @name useFeeds
 * @memberof module:IntegrationsActions
 * @returns {SWRObject} SWR Object containing active feeds data
 */
function useFeeds() {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR("/property/feeds?status=1", fetcher);
  return { data, isLoading: !error && !data, error };
}

/**
 * Hook to fetch property synchronization errors
 * @function
 * @name useGetPropertyErrors
 * @memberof module:IntegrationsActions
 * @param {Object} params - Parameters for filtering sync errors
 * @param {string} params.date - Start date filter for errors (created__gte)
 * @param {string|number} params.client - Client ID to filter errors
 * @param {number} params.page - Page number for pagination
 * @param {string|number} params.feedId - Feed ID to filter errors
 * @returns {SWRObject} SWR object with property sync error data, loading state, error, and mutate function
 */
function useGetPropertyErrors(params) {
  const fetcher = (url) => apiClient.get(url).then((res) => res?.data);
  const { data, error, mutate } = useSWR(
    `externalsyncerrors?created__gte=${params.date}&client_id=${params.client}&page=${params.page}&feed_id=${params.feedId}`,
    fetcher
  );
  return { data, isLoading: !error && !data, error, mutate };
}

/** * Hook to fetch Hubspot integration status
 * @function
 * @name useHubspotStatus
 * @memberof module:IntegrationsActions
 * @returns {SWRObject} SWR object with Hubspot status data
 */

export const useHubspotStatus = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error } = useSWR("/integrations/hubspot/status", fetcher);

  return { data, error };
};
